import { formatDollars, truncateAddress } from '@kwenta/sdk/utils'
import AlertIcon from 'assets/svg/app/alert-triangle.svg'
import RefreshIcon from 'assets/svg/app/refresh.svg'
import BaseIcon from 'assets/svg/providers/base.svg'
import CopyCheckIcon from 'assets/svg/referrals/copy-check.svg'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import { QRCodeSVG } from 'qrcode.react'
import { type FC, type MouseEvent, memo, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider } from '@kwenta/sdk/types'
import { wei } from '@kwenta/wei'
import DropdownField from 'components/DropdownField'
import Input from 'components/Input/Input'
import Spacer from 'components/Spacer'
import isEqual from 'lodash/isEqual'
import { selectShowModalWithParams } from 'state/app/selectors'
import { fetchBalancesAndAllowances } from 'state/balances/actions'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectWalletBalanceByProvider } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectWallet } from 'state/wallet/selectors'
import styled from 'styled-components'
import media from 'styles/media'
import { MIN_DEPOSIT_AMOUNT, chainOptions, coinOptions } from '../constants'

export const getProviderForCoinAndChain = (coin: string, chain: string) => {
	const coinProviders = coinOptions.find((c) => c.value === coin)?.providers || []
	const chainProviders = chainOptions.find((c) => c.value === chain)?.providers || []

	return coinProviders.find((provider) => chainProviders.includes(provider))
}

const DepositInfo: FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const walletAddress = useAppSelector(selectWallet)
	const modalPrams = useAppSelector(selectShowModalWithParams)?.params

	const walletBalancesByProvider = useAppSelector(selectWalletBalanceByProvider, isEqual)
	const { copyToClipboard, CopyTooltip } = useCopyToClipboard({
		tooltipOffset: { x: -70, y: 20 },
	})

	const balance = useMemo(() => {
		return walletBalancesByProvider.reduce((acc, curr) => {
			return acc.add(wei(curr.totalBalance))
		}, ZERO_WEI)
	}, [walletBalancesByProvider])

	const refetchBalancesAndAllowances = useCallback(() => {
		dispatch(fetchBalancesAndAllowances())
	}, [dispatch])

	const perpsProvider = useAppSelector(selectPerpsProvider)

	const chainsList = useMemo(
		() => chainOptions.filter(({ providers }) => providers.includes(perpsProvider)),
		[perpsProvider]
	)
	const [selectedChain, setSelectedChain] = useState(chainsList[0].value)

	const coinsList = useMemo(
		() =>
			coinOptions.filter(({ providers }) =>
				providers.includes(
					selectedChain === 'Base' ? PerpsProvider.SNX_V3_BASE : PerpsProvider.SNX_V2_OP
				)
			),
		[selectedChain]
	)
	const [selectedCoin, setSelectedCoin] = useState(modalPrams?.asset ?? coinsList[0].value)

	const handleCoinChange = (newCoin: string) => {
		setSelectedCoin(newCoin)

		const provider = getProviderForCoinAndChain(newCoin, selectedChain)

		if (!provider) {
			const coinProviders = coinOptions.find((c) => c.value === newCoin)?.providers || []
			const availableChains = chainOptions.filter((c) =>
				c.providers.some((p) => coinProviders.includes(p))
			)

			if (availableChains.length > 0) {
				setSelectedChain(availableChains[0].value)
			}
		}
	}

	const handleChainChange = (newChain: string) => {
		setSelectedChain(newChain)

		const provider = getProviderForCoinAndChain(selectedCoin, newChain)

		if (!provider) {
			const chainProviders = chainOptions.find((c) => c.value === newChain)?.providers || []
			const availableCoins = coinOptions.filter((c) =>
				c.providers.some((p) => chainProviders.includes(p))
			)

			if (availableCoins.length > 0) {
				setSelectedCoin(availableCoins[0].value)
			}
		}
	}

	return (
		<>
			<FlexDivCol rowGap="20px">
				<DropdownField
					label={t('account.deposit-section.choose-coin')}
					options={coinsList}
					value={selectedCoin}
					onChange={handleCoinChange}
					background="hover"
				/>
				<DropdownField
					label={t('account.deposit-section.choose-chain')}
					options={chainOptions}
					value={selectedChain}
					onChange={handleChainChange}
					background="hover"
				/>
			</FlexDivCol>
			<ImportantMessageContainer columnGap="4px">
				<InfoIconContainer>
					<AlertIcon width={16} height={16} />
				</InfoIconContainer>
				<Body size="small" color="secondary" style={{ lineHeight: '18px' }}>
					<Trans
						i18nKey="account.deposit-funds.important-message"
						components={[<Emphasis key="token" />, <Emphasis key="network" />]}
						values={{ coin: selectedCoin, network: selectedChain }}
					/>
				</Body>
			</ImportantMessageContainer>
			<FlexDivRow columnGap="8px">
				<AddressContainer rowGap="8px" justifyContent="space-between">
					<AddressInputContainer rowGap="8px">
						<FlexDivRowCentered justifyContent="space-between">
							<Body size="small" color="secondary">
								{t('account.deposit-section.evm-address')}
							</Body>
							<NetworkContainer>
								<BaseIcon height={10} width={10} />
								<Body size="small" color="primary">
									Base Network
								</Body>
							</NetworkContainer>
						</FlexDivRowCentered>
						<FlexDivRowCentered
							onClick={(event: MouseEvent) => copyToClipboard(event, walletAddress)}
						>
							<AddressInput value={truncateAddress(walletAddress ?? '', 10, 8)} readOnly />
							<CopyCheckIconContainer justifyContent="flex-start" columnGap="5px">
								<Body size="small" color="primary">
									{t('account.deposit-section.copy-button')}
								</Body>
								<StyledCopyCheckIcon width={12} height={12} />
							</CopyCheckIconContainer>
						</FlexDivRowCentered>
					</AddressInputContainer>
					<BalanceContainer columnGap="8px" onClick={() => refetchBalancesAndAllowances()}>
						<FlexDivRow columnGap="8px">
							<Body size="small" color="secondary">
								{t('account.deposit-funds.current-balance')}
							</Body>
							<Body size="small" color="secondary">
								{formatDollars(balance)}
							</Body>
						</FlexDivRow>
						<RefreshIcon width={12} height={12} />
					</BalanceContainer>
				</AddressContainer>
				{walletAddress && (
					<QRCodeSVGContainer
						size={90}
						value={walletAddress}
						level="Q"
						marginSize={2}
						imageSettings={{
							src: '/images/favicon.svg',
							height: 16,
							width: 16,
							excavate: true,
						}}
					/>
				)}
			</FlexDivRow>
			<Divider height={1} divider />
			<FlexDivRowCentered>
				<Body color="secondary">{t('account.deposit-section.deposit-auto-channeled-to')}</Body>
				<Body color="primary" weight="semiBold" mono>
					{t('account.deposit-section.unified-synthetix-account')}
				</Body>
			</FlexDivRowCentered>
			<FlexDivRowCentered>
				<Body color="secondary">{t('account.deposit-section.minimum-deposit-amount')}</Body>
				<Body color="primary" weight="semiBold" mono>
					${MIN_DEPOSIT_AMOUNT} equiv
				</Body>
			</FlexDivRowCentered>
			<MessageContainer>
				<Body size="small" color="secondary" style={{ lineHeight: '18px' }}>
					{t('account.deposit-funds.message')}
				</Body>
			</MessageContainer>
			<CopyTooltip />
		</>
	)
})

const NetworkContainer = styled(FlexDivRowCentered)`
	column-gap: 8px;
	justify-content: flex-start;
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.dropdown.hover.background};
	border-radius: 8px;
	padding: 2px 8px;
	width: fit-content;
`

const BalanceContainer = styled(FlexDivRowCentered)`
	column-gap: 8px;
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	border-radius: 8px;
	padding: 4px 12px;
	width: fit-content;
	cursor: pointer;

	${media.lessThan('md')`
		width: 100%;
	`}
`

const CopyCheckIconContainer = styled(FlexDivRowCentered)`
	position: absolute;
	right: 2%;
	cursor: pointer;
`

const StyledCopyCheckIcon = styled(CopyCheckIcon)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
`

const QRCodeSVGContainer = styled(QRCodeSVG)`
	border-radius: 8px;
`

const AddressInput = styled(Input)`
	width: 100%;
	height: 35px;
	border-radius: 8px;
	font-size: 11px;
	cursor: pointer;
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.dropdown.hover.background};
`

const AddressContainer = styled(FlexDivCol)`
	flex: auto;
`

const AddressInputContainer = styled(FlexDivCol)`
	position: relative;
`

const ImportantMessageContainer = styled(FlexDivRow)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.highlight.background};
	flex: none;
	order: 0;
	flex-grow: 0;
	text-align: left;
	font-size: 12px;
	padding: 10px 12px;
	border-radius: 4px;
	cursor: default;
	white-space: pre-line;
`

const MessageContainer = styled(FlexDivRow)`
	text-align: left;
	padding: 10px 12px;
	border-radius: 4px;
	background: rgba(255, 255, 255, 0.04);
`

const InfoIconContainer = styled.div`
	width: 5%;
	margin-top: 2px;
`

const Emphasis = styled.span`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	font-weight: 600;
`

const Divider = styled(Spacer)`
	width: 100%;
`

export default DepositInfo
